import { Box } from "@mantine/core";

import type { EmbedParams } from "@mountayaapp/markdoc";

import { DEMO_ITINERARY_ID, DEMO_PUBLISHABLE_KEY } from "../../../config/rules/demo";
import { ROOT_DOMAIN } from "../../../config/rules/endpoints";

/**
 * MapEmbeddingProps configure the {% map-embedding %} tag — a live iframe of the
 * hosted embed, part of Map Publishing. The embed's own params are inherited from the generated `EmbedParams`, read
 * out of the schema `@mountaya/elements/studio` publishes, so this interface cannot
 * fall behind the route the way its hand-written predecessor did. The three fields
 * below are the frame's rather than the embed's: `height` and `title` frame the
 * iframe, and `view` seeds the initial map view through the URL hash.
 */
interface MapEmbeddingProps extends EmbedParams {
  height?: number;
  title?: string;
  view?: string;
}

// The hosted embed's route on the application root.
const EMBED_PATH = "/studio/embed";

/**
 * buildEmbedUrl assembles the hosted embed's iframe `src` from the embed params.
 * The publishable key leads (the demo key unless overridden), then the itinerary
 * (the demo one unless overridden, and droppable with `itinerary_id=""`); every
 * other present param becomes a query param, with arrays expanded to repeated params; `view` is
 * excluded from the query and instead placed in the URL hash (`#view=…`), where
 * the embedded studio reads the initial MapLibre view. Absent params are dropped
 * so the embed falls back to its own defaults.
 */
const buildEmbedUrl = (params: Omit<MapEmbeddingProps, "height" | "title">): string => {
  const { itinerary_id, publishable_key, view, ...rest } = params;

  const query = new URLSearchParams();
  query.append("publishable_key", publishable_key ?? DEMO_PUBLISHABLE_KEY);
  // The content selector, defaulted for the same reason the key is: an author writes a tag
  // about a control, not about which itinerary it happens to sit over. Every frame on this
  // site drew a bare basemap until this existed — accurate, and a poor illustration of a
  // product whose subject is an itinerary. `itinerary_id=""` opts back out.
  if (itinerary_id !== "") {
    query.append("itinerary_id", itinerary_id ?? DEMO_ITINERARY_ID);
  }

  for (const [key, value] of Object.entries(rest)) {
    if (value === undefined) {
      continue;
    }

    if (Array.isArray(value)) {
      for (const entry of value) {
        query.append(key, String(entry));
      }
    } else {
      query.append(key, String(value));
    }
  }

  const base = `${ROOT_DOMAIN}${EMBED_PATH}?${query.toString()}`;

  return view ? `${base}#view=${view}` : base;
};

/**
 * MapEmbedding is the {% map-embedding %} renderer the documentation app injects.
 * It renders a live iframe of the hosted embed — a static element with a build-time
 * URL, so it is server-rendering safe and needs no client-only gate; the browser
 * defers loading until the frame nears the viewport.
 */
export const MapEmbedding = ({ height = 480, title, ...params }: MapEmbeddingProps) => (
  // mantine:exempt — framed embed: a 1px theme-border, a theme radius, and a
  // full-width fill on the iframe; Mantine has no border-side shortcut prop.
  <Box
    component="iframe"
    height={height}
    loading="lazy"
    my="md"
    src={buildEmbedUrl(params)}
    style={{
      border: "1px solid var(--mantine-color-default-border)",
      borderRadius: "var(--mantine-radius-md)",
      width: "100%",
    }}
    title={title}
  />
);
